import { NgClass } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { ButtonModule } from 'primeng/button';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { filter } from 'rxjs';
import { ReviewSelectedChallangesComponent } from 'src/app/core/common/components/review-selected-challanges/review-selected-challanges.component';
import { SelectInputSolutionFormKey } from 'src/app/core/common/enums/select-solution.enum';
import { DataEntryCategory } from 'src/app/core/common/models/master-data/data-entry';
import {
  DiagnosisDataEntry,
  DiagnosisDataEntryCreateDto,
  DiagnosisDataEntryGetDto,
} from 'src/app/core/common/models/workflow/diagnosis-entry';
import { DiagnosisDataEntryService } from 'src/app/core/services/diagnosis-data-entry.service';
import { DiagnosisSolutionService } from 'src/app/core/services/diagnosis-solution.service';
import { StoreService } from 'src/app/core/services/global-store/store.service';
import { setAvailableSolutions } from 'src/app/core/store/actions/select-solution.action';
import { LoadingDirective } from '../../../../../core/common/directives/loading/loading.directive';
import { DialogCommonService } from '../../../../../core/services/common/dialog-common.service';

@Component({
  selector: 'app-input-solutions',
  imports: [
    ReactiveFormsModule,
    DropdownModule,
    FormsModule,
    NgClass,
    InputTextModule,
    ReviewSelectedChallangesComponent,
    ButtonModule,
    SelectModule,
    LoadingDirective,
  ],
  templateUrl: './input-solutions.component.html',
  styleUrl: './input-solutions.component.scss',
})
export class InputSolutionsComponent implements OnInit {
  readonly #globalStore = inject(Store);
  storeService = inject(StoreService);
  fb = inject(FormBuilder);
  dataEntryService = inject(DiagnosisDataEntryService);
  diagnosisSolutionService = inject(DiagnosisSolutionService);
  dialogCommonService = inject(DialogCommonService);

  @Input() diagnosisSessionId: string = '';

  inputSolution!: FormGroup;
  isDisabledDropdown = signal(false);
  isEnableClearAllBtn = signal(false);

  requiredInputs: {
    [category: string]: {
      label: string;
      value: DiagnosisDataEntryCreateDto | string;
    }[];
  } = {};
  entryCategories: DataEntryCategory[] = [];
  #destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.inputSolution = this.fb.group({});
    this.dataEntryService
      .getSuggestedDataEntryCategories(this.diagnosisSessionId)
      .pipe(filter((dataEntryCategories) => !!dataEntryCategories))
      .subscribe((dataEntryCategories) => {
        this.entryCategories = dataEntryCategories;
        this.renderRequiredInputs(dataEntryCategories);
        this.loadEnteredEntryValues();
      });

    this.storeService
      .getSelectedSolution()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((isSolutionSelected) => {
        this.disableInputFields(isSolutionSelected);
        this.isEnableClearAllBtn.set(
          isSolutionSelected
            ? !isSolutionSelected
            : this.checkFormControlValues(),
        );
      });
  }

  loadEnteredEntryValues() {
    this.dataEntryService
      .verifyEnteredValues(this.diagnosisSessionId)
      .pipe(filter((enteredValues) => !!enteredValues))
      .subscribe((enteredValues) => {
        enteredValues.forEach((value) => {
          this.setEntryValue(value.dataEntryCategoryName, value);
        });
      });
  }

  onDropdownChanged(event: DropdownChangeEvent, entry: DataEntryCategory) {
    if (event.value === null) this.clearEntryValue(entry);
    else {
      const isConditionExisted = entry.solutionCondition;

      if (isConditionExisted) {
        const inputControl = this.inputSolution.get(`input${entry.name}`);
        if (inputControl) {
          this.updateInputValue(entry, inputControl.value, true);
        }
      } else {
        const changedValue: DiagnosisDataEntryCreateDto = event.value;
        const entryValue: DiagnosisDataEntryCreateDto = {
          diagnosisId: this.diagnosisSessionId,
          dataEntryCategoryId: changedValue.dataEntryCategoryId,
          dataEntryValueId: changedValue.dataEntryValueId,
        };
        this.updateEntryValue(entryValue);
      }
    }
    this.isEnableClearAllBtn.set(this.checkFormControlValues());
  }

  onTextInputChanged(event: any, entry: DataEntryCategory) {
    this.updateInputValue(entry, event.target.value || '', false);
    this.isEnableClearAllBtn.set(this.checkFormControlValues());
  }

  updateInputValue(
    entry: DataEntryCategory,
    value: string | null,
    changeUoMOnly: boolean,
  ) {
    if (entry.solutionCondition) {
      const defaultUoM = entry.solutionCondition.uoMCategory.uoMs.filter(
        (uom) => uom.isDefault === true,
      );

      if (value === null) return;

      if (defaultUoM.length > 0 && entry.dataEntryValues.length > 0) {
        const selectedUoM = this.inputSolution.get(entry.name);
        const entryValue: DiagnosisDataEntryCreateDto = {
          diagnosisId: this.diagnosisSessionId,
          dataEntryCategoryId: entry.id,
          dataEntryValueId: entry.dataEntryValues[0].id,
          inputValue: value?.toString(),
          uoMId:
            selectedUoM !== null && selectedUoM.value !== null
              ? selectedUoM.value.uoMId
              : defaultUoM[0].id,
        };

        if (value.toString().trim()) {
          if (changeUoMOnly) {
            return this.changeUoM(entryValue);
          }
          this.updateEntryValue(entryValue);
        } else this.clearEntryValue(entry);
      }
    }
  }

  clearEntryValue(value: DataEntryCategory) {
    const removedValue: DiagnosisDataEntryGetDto = {
      diagnosisId: this.diagnosisSessionId,
      dataEntryCategoryId: value.id,
      dataEntryValueId: value.id,
    };

    this.dataEntryService.removeEntryValue(removedValue).subscribe({
      next: (availableSolutions) => {
        this.#globalStore.dispatch(
          setAvailableSolutions({
            availableSolutions,
          }),
        );
        this.setEntryValue(value.name, undefined);
      },
      error: (err: Error) => this.showBlockStateWarning(err.message),
    });
  }

  updateEntryValue(value: DiagnosisDataEntryCreateDto) {
    this.dataEntryService.upsertEntryValue(value).subscribe({
      next: (availableSolutions) => {
        this.#globalStore.dispatch(
          setAvailableSolutions({
            availableSolutions,
          }),
        );
      },
      error: (err: Error) => this.showBlockStateWarning(err.message),
    });
  }

  changeUoM(value: DiagnosisDataEntryCreateDto) {
    this.dataEntryService.changeUoM(value).subscribe({
      next: (res) => {
        const inputControl = this.inputSolution.get(
          `input${res.dataEntryCategoryName}`,
        );
        if (inputControl)
          inputControl.setValue(res.convertedValue ?? res.inputValue);
      },
      error: (err: Error) => this.showBlockStateWarning(err.message),
    });
  }

  getDefaultValue(entry: DataEntryCategory): string {
    if (entry.solutionCondition) {
      const defaultUoM = entry.solutionCondition.uoMCategory.uoMs.filter(
        (uom) => uom.isDefault === true,
      );
      if (defaultUoM.length > 0) {
        return defaultUoM[0].name;
      }
    }

    return 'Select';
  }

  disableInputFields(isSelectedSolution: boolean): void {
    if (isSelectedSolution) {
      this.inputSolution.disable();
    } else {
      this.inputSolution.enable();
    }

    this.isDisabledDropdown.set(isSelectedSolution);
  }

  renderRequiredInputs(dataEntryCategories: DataEntryCategory[]): void {
    dataEntryCategories.forEach((cate) => {
      if (cate.solutionCondition || cate.dataEntryValues.length) {
        if (
          this.isInputControl(cate.inputType.uiControlName!) &&
          cate.solutionCondition
        ) {
          const inputCate = `input${cate.name}`;
          this.requiredInputs[inputCate] = [
            {
              label: inputCate,
              value: '0',
            },
          ];
          this.inputSolution.addControl(
            inputCate,
            new FormControl(null, Validators.required),
          );

          this.requiredInputs[cate.name] =
            cate.solutionCondition.uoMCategory.uoMs.map((value) => {
              return {
                label: value.name,
                value: {
                  diagnosisId: this.diagnosisSessionId,
                  dataEntryCategoryId: cate.id,
                  dataEntryValueId: cate.dataEntryValues[0].id,
                  uoMId: value.id,
                },
              };
            });
        } else if (
          this.isDropdownControl(cate.inputType.uiControlName!) &&
          cate.dataEntryValues.length > 0
        ) {
          this.requiredInputs[cate.name] = cate.dataEntryValues.map((value) => {
            return {
              label: value.name,
              value: {
                diagnosisId: this.diagnosisSessionId,
                dataEntryCategoryId: cate.id,
                dataEntryValueId: value.id,
              },
            };
          });
        }

        this.inputSolution.addControl(
          cate.name,
          new FormControl(null, Validators.required),
        );
      }
    });
  }

  setEntryValue(entryCategory: string, value?: DiagnosisDataEntry) {
    const cate = this.inputSolution.get(entryCategory);
    if (cate) {
      if (!value) {
        cate.reset();
        const inputControl = this.inputSolution.get(`input${entryCategory}`);
        if (inputControl) inputControl.reset();
        return;
      }

      if (value.inputValue) {
        const inputControl = this.inputSolution.get(`input${entryCategory}`);
        if (inputControl) inputControl.setValue(value.inputValue ?? '');

        if (value.uoM) cate.setValue(this.adaptToUoM(value));
      } else {
        cate.setValue(this.adaptToDropdown(value));
      }
    }
  }

  adaptToDropdown(dataEntry: DiagnosisDataEntry): DiagnosisDataEntryCreateDto {
    return {
      diagnosisId: dataEntry.diagnosisId,
      dataEntryCategoryId: dataEntry.dataEntryCategoryId,
      dataEntryValueId: dataEntry.dataEntryValueId,
    };
  }

  adaptToUoM(dataEntry: DiagnosisDataEntry): DiagnosisDataEntryCreateDto {
    return {
      diagnosisId: dataEntry.diagnosisId,
      dataEntryCategoryId: dataEntry.dataEntryCategoryId,
      dataEntryValueId: dataEntry.dataEntryValueId,
      uoMId: dataEntry.uoMId,
    };
  }

  isInputControl(uiControlName: string): boolean {
    return (
      uiControlName === SelectInputSolutionFormKey.InputDouble ||
      uiControlName === SelectInputSolutionFormKey.InputInteger
    );
  }

  isDropdownControl(uiControlName: string): boolean {
    return uiControlName === SelectInputSolutionFormKey.DropDown;
  }

  countControls = (control: AbstractControl): number => {
    if (control instanceof FormControl) {
      return 1;
    }

    if (control instanceof FormArray) {
      return control.controls.reduce(
        (acc, curr) => acc + this.countControls(curr),
        0,
      );
    }

    if (control instanceof FormGroup) {
      return Object.keys(control.controls)
        .map((key) => control.controls[key])
        .reduce((acc, curr) => acc + this.countControls(curr), 0);
    }

    return 0;
  };

  clearAllSelections() {
    this.dataEntryService.resetEntryValue(this.diagnosisSessionId).subscribe({
      next: (availableSolutions) => {
        this.#globalStore.dispatch(
          setAvailableSolutions({
            availableSolutions,
          }),
        );

        this.inputSolution.reset();
        this.isEnableClearAllBtn.set(false);
      },
      error: (err: Error) => this.showBlockStateWarning(err.message),
    });
  }

  checkFormControlValues(): boolean {
    const excludeKeys: string[] = [];
    this.entryCategories.forEach((cate) => {
      if (!this.isDropdownControl(cate.inputType.uiControlName!)) {
        excludeKeys.push(cate.name!);
      }
    });
    const keysToCheck = Object.keys(this.inputSolution.controls).filter(
      (key) => !excludeKeys.includes(key),
    );
    return keysToCheck.some((key) => this.inputSolution.controls[key]?.value);
  }

  showBlockStateWarning(message: string): void {
    this.dialogCommonService.show(true, {
      title: 'Blocking session',
      message,
      closeLabel: 'Close',
      closable: false,
    });
  }
}
